import {useEffect, useState, useCallback} from 'react';
import {
    Report,
    fetchAllReports,
    createReport as apiCreateReport,
    deleteReport as apiDeleteReport,
    executeReport as apiExecuteReport,
    ReportCreateRequest, ReportExecutionResult,
} from "../api/ReportApi";
import {ApiError} from "../api/types";


interface UseReportsProps {
    maxItems?: number;
}

interface UseReportsReturn {
    reports: Report[];
    loading: boolean;
    error: string | null;

    // Action states
    creating: boolean;
    deleting: boolean;
    executing: boolean;

    // Actions
    createReport: (request: ReportCreateRequest) => Promise<Report>;
    deleteReport: (reportId: string) => Promise<void>;
    executeReport: (reportId: string) => Promise<ReportExecutionResult>;
    refreshReports: () => Promise<void>;
}

export function useReports({maxItems}: UseReportsProps = {}): UseReportsReturn {

    const [reports, setReports] = useState<Report[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Individual action states
    const [creating, setCreating] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);
    const [executing, setExecuting] = useState<boolean>(false);

    useEffect(() => {
        loadReports();
    }, []);

    const loadReports = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchAllReports();
            setReports(response.data || []);

        } catch (error) {
            console.error('Error loading reports:', error);
            setError('Failed to load reports');
            setReports([]);

        } finally {
            setLoading(false);
        }
    };

    const refreshReports = useCallback(async () => {
        await loadReports();
    }, []);

    // Private: Add template to list
    const addReport = useCallback((template: Report) => {
        setReports(prevTemplates => {
            const newTemplates = [template, ...prevTemplates];
            return maxItems ? newTemplates.slice(0, maxItems) : newTemplates;
        });
    }, [maxItems]);

    // Private: Remove template from list
    const removeReport = useCallback((templateId: string) => {
        setReports(prevTemplates =>
            prevTemplates.filter(template => template.id !== templateId)
        );
    }, []);

    // Private: Update template in list
    const updateReport =
        useCallback((templateId: string, updates: Partial<Report>) => {

            setReports(prevTemplates =>
                prevTemplates.map(template =>
                    template.id === templateId ? {...template, ...updates} : template
                )
            );
        }, []);

    // Public: Create report (API call + add to list)
    const createReport = useCallback(async (request: ReportCreateRequest): Promise<Report> => {
        setCreating(true);
        try {
            const response = await apiCreateReport(request);
            if (response.success && response.data) {
                addReport(response.data);
                return response.data;

            } else {
                throw new ApiError(response.error, 'Failed to create template');
            }

        } catch (err) {
            console.error('Error creating template:', err);
            throw err;

        } finally {
            setCreating(false);
        }
    }, [addReport]);

    // Public: Delete template (API call + remove from list)
    const deleteReport = useCallback(async (reportId: string) => {
        setDeleting(true);
        try {
            const response = await apiDeleteReport(reportId);

            if (response.success) {
                removeReport(reportId);  // Internal update

            } else {
                throw new ApiError(response.error, 'Failed to delete report'); // TODO: Do not throw in try {}
            }

        } catch (err) {
            console.error('Error deleting template:', err);
            throw err;

        } finally {
            setDeleting(false);
        }
    }, [removeReport]);

    const executeReport = useCallback(async (reportId: string): Promise<ReportExecutionResult> => {
        setExecuting(true);
        try {
            const response = await apiExecuteReport(reportId);
            if (response.success && response.data) {
                return response.data;

            } else {
                throw new ApiError(response.error, 'Failed to execute report');
            }

        } catch (err) {
            console.error('Error executing report:', err);
            throw err;

        } finally {
            setExecuting(false);
        }
    }, [addReport]);

    return {
        reports,
        loading,
        error,
        creating,
        deleting,
        executing,
        createReport,
        deleteReport,
        executeReport,
        refreshReports
    };
}